import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { useT } from '@/store/i18n';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';

interface Tag {
  id: number;
  name: string;
  usage_count?: number;
}

interface Props {
  value: number[];
  onChange: (ids: number[]) => void;
}

const TAG_PALETTE = [
  'bg-sky-100 text-sky-700',
  'bg-violet-100 text-violet-700',
  'bg-amber-100 text-amber-700',
  'bg-emerald-100 text-emerald-700',
  'bg-rose-100 text-rose-700',
  'bg-indigo-100 text-indigo-700',
  'bg-teal-100 text-teal-700',
  'bg-orange-100 text-orange-700',
];

function tagColor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h << 5) - h + name.charCodeAt(i);
  return TAG_PALETTE[Math.abs(h) % TAG_PALETTE.length];
}

export function TagInput({ value, onChange }: Props) {
  const t = useT();
  const qc = useQueryClient();
  const [input, setInput] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: allTags = [] } = useQuery<Tag[]>({
    queryKey: ['tags'],
    queryFn: async () => {
      const r = await api<{ data?: { tags?: Tag[] } }>('tags/index.php');
      return r.data?.tags ?? [];
    },
    staleTime: 60_000,
  });

  const createTag = useMutation({
    mutationFn: async (name: string) => {
      const r = await api<{ data?: { tag?: Tag } }>('tags/index.php', {
        method: 'POST',
        json: { name },
      });
      return r.data?.tag ?? null;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tags'] }),
  });

  const selectedTags = allTags.filter((t) => value.includes(t.id));

  const filtered = allTags.filter(
    (tag) =>
      !value.includes(tag.id) &&
      tag.name.toLowerCase().includes(input.toLowerCase())
  );

  function remove(id: number) {
    onChange(value.filter((v) => v !== id));
  }

  async function selectTag(tag: Tag) {
    if (!value.includes(tag.id)) {
      onChange([...value, tag.id]);
    }
    setInput('');
    setOpen(false);
  }

  async function handleEnter() {
    const trimmed = input.trim();
    if (!trimmed) return;
    const exact = allTags.find(
      (t) => t.name.toLowerCase() === trimmed.toLowerCase()
    );
    if (exact) {
      await selectTag(exact);
      return;
    }
    const optimisticName = trimmed;
    setInput('');
    setOpen(false);
    const created = await createTag.mutateAsync(optimisticName);
    if (created) {
      onChange([...value, created.id]);
    }
  }

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  return (
    <div ref={containerRef}>
      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
        {t('tag.field_label')}
      </label>
      <div
        className="min-h-[40px] flex flex-wrap items-center gap-1.5 px-2.5 py-1.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 focus-within:ring-2 focus-within:ring-brand-500 focus-within:border-brand-500 cursor-text"
        onClick={() => inputRef.current?.focus()}
      >
        {selectedTags.map((tag) => (
          <span
            key={tag.id}
            className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium', tagColor(tag.name))}
          >
            {tag.name}
            <button
              type="button"
              aria-label={t('btn.remove')}
              onClick={(e) => { e.stopPropagation(); remove(tag.id); }}
              className="hover:opacity-70 transition-opacity"
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={input}
          placeholder={selectedTags.length === 0 ? t('tag.input_ph') : ''}
          className="flex-1 min-w-[120px] outline-none bg-transparent text-sm text-slate-800 dark:text-slate-200 placeholder-slate-400 py-0.5"
          onChange={(e) => { setInput(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); handleEnter(); }
            if (e.key === 'Escape') setOpen(false);
            if (e.key === 'Backspace' && input === '' && value.length > 0) {
              onChange(value.slice(0, -1));
            }
          }}
        />
      </div>

      {open && (filtered.length > 0 || (input.trim() && !allTags.find((t) => t.name.toLowerCase() === input.trim().toLowerCase()))) && (
        <div className="relative z-20">
          <div className="absolute top-1 left-0 right-0 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg py-1 max-h-48 overflow-y-auto">
            {filtered.slice(0, 20).map((tag) => (
              <button
                key={tag.id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => selectTag(tag)}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-2"
              >
                <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', tagColor(tag.name))}>
                  {tag.name}
                </span>
                {tag.usage_count != null && tag.usage_count > 0 && (
                  <span className="text-xs text-slate-400 ml-auto">{tag.usage_count}</span>
                )}
              </button>
            ))}
            {input.trim() && !allTags.find((tt) => tt.name.toLowerCase() === input.trim().toLowerCase()) && (
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleEnter}
                className="w-full text-left px-3 py-1.5 text-sm text-brand-700 hover:bg-brand-50 dark:hover:bg-brand-950/30 flex items-center gap-1.5"
              >
                <span className="text-xs font-medium">{t('tag.create_new')}</span>
                <span className="font-semibold">"{input.trim()}"</span>
              </button>
            )}
          </div>
        </div>
      )}

      <p className="text-xs text-slate-400 mt-1">{t('tag.hint')}</p>
    </div>
  );
}
